import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, ChefHat, UtensilsCrossed, ArrowLeft, Bell } from "lucide-react";
import api, { formatIDR, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageState } from "@/components/ui/page-state";

function menuUrl(order) {
  const params = new URLSearchParams();
  params.set('outlet', order.outlet_slug);
  if (order.table_number) params.set('tableNumber', order.table_number);
  if (order.order_type === 'room') params.set('type', 'room');
  return `/order?${params.toString()}`;
}


const STATUS_STEPS = [
  { key: "pending", label: "Menunggu Konfirmasi", icon: Clock },
  { key: "preparing", label: "Sedang Disiapkan", icon: ChefHat },
  { key: "served", label: "Disajikan", icon: UtensilsCrossed },
  { key: "completed", label: "Selesai", icon: CheckCircle2 },
];

const PAYMENT_METHOD_LABELS = { cash: "Bayar di Kasir", qris: "QRIS", transfer: "Transfer Bank" };

function paymentStatusBadge(status) {
  if (status === "paid") return { label: "Lunas", className: "bg-success/10 text-success" };
  if (status === "refunded") return { label: "Refund", className: "bg-muted text-muted-foreground" };
  return { label: "Belum Bayar", className: "bg-amber-500/10 text-amber-800" };
}

export default function OrderStatusPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [outlet, setOutlet] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const [callingWaiter, setCallingWaiter] = useState(false);
  const [waiterCalled, setWaiterCalled] = useState(false);

  useEffect(() => {
    let mounted = true;
    setOrder(null);
    setLoadError("");
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (mounted) { setOrder(data); setLoadError(""); }
      } catch {
        if (mounted) setLoadError("Gagal memuat status pesanan.");
      }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => { mounted = false; clearInterval(interval); };
  }, [orderId, retry]);

  useEffect(() => {
    if (!order?.outlet_slug) return;
    (async () => {
      try {
        const { data } = await api.get(`/outlets/by-slug/${order.outlet_slug}`);
        setOutlet(data);
      } catch {}
    })();
  }, [order?.outlet_slug]);

  if (!order) return <PageState kind={loadError ? "error" : "loading"} title={loadError ? "Pesanan belum dapat dimuat" : "Memuat pesanan"} description={loadError ? "Periksa koneksi dan tautan pesanan Anda, lalu coba lagi." : "Menyiapkan informasi pesanan Anda…"} onRetry={loadError ? () => setRetry((value) => value + 1) : undefined} className="min-h-screen" />;

  const activeIdx = STATUS_STEPS.findIndex((s) => s.key === order.status);
  const subtotal = order.subtotal || order.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const logoSrc = outlet?.logo_file_id ? fileUrl(outlet.logo_file_id) : (outlet?.logo_url || "");
  const paymentMethodKey = order.payment_method || "cash";
  const paymentStatusKey = order.payment_status || "unpaid";
  const paymentBadge = paymentStatusBadge(paymentStatusKey);
  const qrisSrc = outlet?.qris_image_file_id ? fileUrl(outlet.qris_image_file_id) : "";

  const callWaiter = async () => {
    setCallingWaiter(true);
    try {
      await api.post("/waiter-calls", {
        order_id: order.id,
        reason: "Panggil Pelayan"
      });
      setWaiterCalled(true);
      setTimeout(() => setWaiterCalled(false), 3000);
    } catch (err) {
      console.error("Failed to call waiter:", err);
    } finally {
      setCallingWaiter(false);
    }
  };

  return (
    <div className="customer-shell">
      <main className="max-w-[480px] mx-auto p-4 sm:p-6 sm:py-10">
        <Link to={menuUrl(order)} className="inline-flex items-center gap-1 text-sm text-muted-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Kembali ke menu
        </Link>

        <div className="surface-card p-5 md:p-6">
          {outlet && (
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
              <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center overflow-hidden">
                {logoSrc ? <img src={logoSrc} alt="" className="w-full h-full object-contain p-1"/> : <span className="font-bold text-primary text-sm">{outlet.name?.slice(0,1)}</span>}
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold text-sm truncate">{outlet.name}</p>
                {outlet.tagline && <p className="text-xs text-muted-foreground truncate">{outlet.tagline}</p>}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Pesanan</p>
              <h1 className="font-display text-xl md:text-2xl font-bold" data-testid="order-number">#{order.order_number}</h1>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{(order.order_type === 'room' ? 'Kamar' : 'Meja')}</p>
              <p className="font-display text-2xl font-bold text-primary" data-testid="order-table">{order.table_number}</p>
            </div>
          </div>

          <div className="mt-6 relative">
            {STATUS_STEPS.map((step, idx) => {
              const done = idx <= activeIdx;
              const Icon = step.icon;
              return (
                <div key={step.key} className="flex items-center gap-3 py-2">
                  <div className={`w-10 h-10 rounded-full grid place-items-center transition-colors duration-300 ${
                    done ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${done ? "font-medium text-foreground" : "text-muted-foreground"}`}>{step.label}</p>
                  </div>
                  {idx === activeIdx && order.status !== "cancelled" && (
                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                  )}
                </div>
              );
            })}
          </div>

          {order.status === "cancelled" && (
            <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">Pesanan dibatalkan</div>
          )}
          {waiterCalled && (
            <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm border border-green-200">
              ✓ Permintaan pelayan telah dikirim
            </div>
          )}
        </div>

        <Button 
          onClick={callWaiter} 
          disabled={callingWaiter || order.status === "cancelled"}
          className="w-full mt-4 h-11 rounded-full bg-amber-700 hover:bg-amber-800 text-white"
        >
          <Bell className="w-4 h-4 mr-2" />
          {callingWaiter ? "Memanggil..." : "Panggil Pelayan"}
        </Button>

        {/* WhatsApp feature temporarily disabled */}

        <div className="surface-card p-5 md:p-6 mt-4">
          <h2 className="font-display font-bold text-lg mb-3">Detail Pesanan</h2>
          <div className="space-y-2">
            {order.items.map((it, i) => (
              <div key={i} className="flex items-start justify-between text-sm gap-3">
                <div className="min-w-0">
                  <p className="font-medium">{it.quantity}× {it.name}</p>
                  {it.notes && <p className="text-xs italic text-muted-foreground">"{it.notes}"</p>}
                </div>
                <span className="font-medium whitespace-nowrap">{formatIDR(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-4 pt-3 space-y-1.5 text-xs">
            <Row label="Subtotal" value={formatIDR(subtotal)} muted/>
            {(order.discount_amount || 0) > 0 && (
              <div className="flex items-center justify-between" data-testid="status-discount-row">
                <span className="text-success font-medium">
                  Diskon{order.discount_type === "percent" && order.discount_value ? ` (${order.discount_value}%)` : ""}
                </span>
                <span className="text-success font-medium">−{formatIDR(order.discount_amount)}</span>
              </div>
            )}
            {(order.tax_percent > 0 || order.tax_amount > 0) && (
              <Row label={`Pajak (${order.tax_percent || 0}%)`} value={formatIDR(order.tax_amount || 0)} muted/>
            )}
            {(order.service_percent > 0 || order.service_amount > 0) && (
              <Row label={`Service (${order.service_percent || 0}%)`} value={formatIDR(order.service_amount || 0)} muted/>
            )}
          </div>
          <div className="border-t border-border mt-3 pt-3 flex items-center justify-between">
            <span className="font-medium">Total</span>
            <span className="font-sans text-xl font-bold text-heritage tabular-nums" data-testid="order-total">{formatIDR(order.total)}</span>
          </div>

          {/* ── Info Pembayaran ── */}
          <div className="mt-3 border border-border rounded-xl p-3 space-y-2" data-testid="payment-info-box">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Metode Pembayaran</span>
              <span className="font-medium" data-testid="payment-method-label">{PAYMENT_METHOD_LABELS[paymentMethodKey] || PAYMENT_METHOD_LABELS.cash}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Status Pembayaran</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${paymentBadge.className}`} data-testid="payment-status-badge">
                {paymentBadge.label}
              </span>
            </div>
            {paymentStatusKey === "unpaid" && paymentMethodKey === "qris" && qrisSrc && (
              <div className="flex flex-col items-center gap-2 bg-accent/40 rounded-lg p-3 pt-4" data-testid="qris-payment-box">
                <img src={qrisSrc} alt="QRIS" className="w-44 h-44 object-contain bg-white rounded-lg border border-border p-1" data-testid="qris-payment-image"/>
                <p className="text-[11px] text-muted-foreground text-center">
                  Scan QRIS di atas dari aplikasi bank / e-wallet Anda sebesar <b>{formatIDR(order.total)}</b>, lalu tunjukkan bukti ke kasir.
                </p>
              </div>
            )}
            {paymentStatusKey === "unpaid" && paymentMethodKey === "transfer" && outlet?.transfer_info && (
              <div className="bg-accent/40 rounded-lg p-3" data-testid="transfer-payment-box">
                <p className="text-[11px] text-muted-foreground mb-1">Transfer sebesar <b>{formatIDR(order.total)}</b> ke:</p>
                <p className="text-sm font-medium whitespace-pre-line" data-testid="transfer-payment-info">{outlet.transfer_info}</p>
                <p className="text-[11px] text-muted-foreground mt-1">Tunjukkan bukti transfer ke kasir.</p>
              </div>
            )}
            {paymentStatusKey === "paid" && (
              <p className="text-[11px] text-success font-medium" data-testid="payment-paid-note">✓ Pembayaran Anda telah dikonfirmasi. Terima kasih!</p>
            )}
          </div>

          {order.notes && (
            <div className="mt-3 p-3 bg-accent rounded-lg text-xs">
              <span className="text-muted-foreground">Catatan: </span>{order.notes}
            </div>
          )}
          {outlet?.payment_note && paymentMethodKey === "cash" && (
            <p className="text-[11px] text-muted-foreground italic mt-3">{outlet.payment_note}</p>
          )}
        </div>

        <Link to={menuUrl(order)}>
          <Button className="w-full mt-4 h-11 rounded-full" variant="outline">Pesan Lagi</Button>
        </Link>
      </main>
    </div>
  );
}

function Row({ label, value, muted }) {  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-muted-foreground" : ""}>{label}</span>
      <span className={muted ? "text-muted-foreground" : "font-medium"}>{value}</span>
    </div>
  );
}
